import { CUSTOMER_TABS, type CustomerCardState } from '../types/CustomerCardTypes';
import type { CardSearchState } from '../types/search.types';
import { CARD_OPERATION_STEP } from '../constants';
import {
    SEED_CUSTOMER_CARD,
    CUSTOMER_CARD_APPLY_STARTED,
    CUSTOMER_CARD_APPLY_SUCCEEDED,
    CUSTOMER_CARD_APPLY_FAILED,
    CUSTOMER_CARD_CANCELLED,
    SET_INSURANCE_PROVIDERS,
    SAVE_PARTICIPANTS_REQUEST,
    SAVE_PARTICIPANTS_SUCCESS,
    SAVE_PARTICIPANTS_FAILURE,
    SWAP_BUYER_COBUYER_FAILURE,
    SWAP_BUYER_COBUYER_REQUEST,
    SWAP_BUYER_COBUYER_RESET,
    SWAP_BUYER_COBUYER_SUCCESS,
    DROP_CO_BUYER_FAILURE,
    DROP_CO_BUYER_REQUEST,
    DROP_CO_BUYER_RESET,
    DROP_CO_BUYER_SUCCESS,
    SAVE_INSURANCE_REQUEST,
    SAVE_INSURANCE_SUCCESS,
    SAVE_INSURANCE_FAILURE
} from './actions';
import {
    START_OPERATION,
    CONTINUE_OPERATION,
    BACK,
    CANCEL_CARD_OPERATION,
    SEARCH_REQUEST,
    SEARCH_SUCCESS,
    SEARCH_FAILURE,
    SELECT_SEARCH_RESULT,
    CLEAR_SEARCH_RESULTS
} from './cardOperationActions';
import type { CustomerCardAction } from './actionCreators';
import type { CardOperationAction } from './cardOperationActionCreators';

const INITIAL_SEARCH_STATE: CardSearchState = {
    results: [],
    loading: false,
    error: null,
    selected: null,
    term: null,
    attemptCount: 0
};

const INITIAL_STATE: CustomerCardState = {
    buyer: undefined,
    coBuyer: null,
    hasCoBuyer: false,
    initialTab: CUSTOMER_TABS.BUYER,
    saveStatus: 'idle',
    swapStatus: 'idle',
    dropCoBuyerStatus: 'idle',
    search: INITIAL_SEARCH_STATE,
    operation: null,
    draft: null
};

type SwapLifecycleAction =
    | { type: typeof SWAP_BUYER_COBUYER_REQUEST }
    | { type: typeof SWAP_BUYER_COBUYER_SUCCESS }
    | { type: typeof SWAP_BUYER_COBUYER_FAILURE; payload: { error: string } }
    | { type: typeof SWAP_BUYER_COBUYER_RESET };

type DropCoBuyerLifecycleAction =
    | { type: typeof DROP_CO_BUYER_REQUEST }
    | { type: typeof DROP_CO_BUYER_SUCCESS }
    | { type: typeof DROP_CO_BUYER_FAILURE; payload: { error: string } }
    | { type: typeof DROP_CO_BUYER_RESET };

export type CustomerCardReducer = (
    state: CustomerCardState | undefined,
    action: CustomerCardAction | CardOperationAction | SwapLifecycleAction | DropCoBuyerLifecycleAction
) => CustomerCardState;

export const customerCardReducer: CustomerCardReducer = (
    state: CustomerCardState = INITIAL_STATE,
    action: CustomerCardAction | CardOperationAction | SwapLifecycleAction | DropCoBuyerLifecycleAction
): CustomerCardState => {
    switch (action.type) {
        case SEED_CUSTOMER_CARD:
            return {
                buyer: action.payload.buyer,
                // An explicit `null` clears the co-buyer (removed outside the card); an omitted key
                // falls back, so a partial seed never drops a co-buyer the slice already holds.
                // `??` cannot express this — it treats null and undefined alike (US2017510 AC 2).
                coBuyer: action.payload.coBuyer !== undefined ? action.payload.coBuyer : state.coBuyer,
                hasCoBuyer: action.payload.hasCoBuyer,
                initialTab: action.payload.initialTab ?? state.initialTab,
                dealId: action.payload.dealId ?? state.dealId,
                dealerId: action.payload.dealerId ?? state.dealerId,
                saveStatus: 'idle',
                swapStatus: 'idle',
                vehicleInsuranceInfo: action.payload.vehicleInsuranceInfo ?? state.vehicleInsuranceInfo ?? null,
                isVehicleInsuranceUpdated: action.payload.isVehicleInsuranceUpdated ?? state.isVehicleInsuranceUpdated ?? false,
                contractApiErroredVehicleInsurance: action.payload.contractApiErroredVehicleInsurance ?? null,
                insuranceProviders: state.insuranceProviders,
                search: INITIAL_SEARCH_STATE,
                operation: null,
                draft: null,
                isDealSoldOrContracted: action.payload.isDealSoldOrContracted ?? state.isDealSoldOrContracted ?? false,
                isContractGenerated: action.payload.isContractGenerated ?? state.isContractGenerated ?? false,
                commonOrgId: action.payload.commonOrgId ?? state.commonOrgId
            };
        case CUSTOMER_CARD_APPLY_STARTED:
            return {
                ...state,
                saveStatus: 'saving',
                saveError: undefined,
                // The banner belongs to the save that granted consent, so a new save attempt retires it.
                creditConsentJustGranted: false
            };
        case CUSTOMER_CARD_APPLY_SUCCEEDED: {
            // A drafted participant replaced someone, so merging would keep the replaced person's
            // fields wherever the new one has none. Only an edit merges.
            const wasReplacement = Boolean(state.draft);
            const mergeParticipant = <T>(existing: T | undefined | null, saved: unknown) =>
                (wasReplacement ? saved : { ...existing, ...(saved as object) }) as T;
            return {
                ...state,
                // Saved form values go back so the form reinitialises with them next time the card opens.
                buyer: action.payload.buyer
                    ? mergeParticipant<CustomerCardState['buyer']>(state.buyer, action.payload.buyer)
                    : state.buyer,
                coBuyer: action.payload.coBuyer
                    ? mergeParticipant<CustomerCardState['coBuyer']>(state.coBuyer, action.payload.coBuyer)
                    : state.coBuyer,
                // A save that carried a co-buyer means the deal has one. The host reseeds only on a
                // closed-then-opened slide-out, so without this the tab vanishes again on success.
                hasCoBuyer: state.hasCoBuyer || Boolean(action.payload.coBuyer),
                vehicleInsuranceInfo: action.payload.insurance ?? state.vehicleInsuranceInfo,
                // Consent banner announces a one-time event, so it keys off the *transition* to
                // granted, not the value. This is the only place both the pre-save and incoming
                // values exist — comparing after the merge above would always look unchanged.
                creditConsentJustGranted:
                    (Boolean(action.payload.buyer?.creditPullConsentGiven) && !state.buyer?.creditPullConsentGiven) ||
                    (Boolean(action.payload.coBuyer?.creditPullConsentGiven) && !state.coBuyer?.creditPullConsentGiven),
                saveStatus: 'success',
                saveError: undefined,
                // The save landed, so the operation is over — whatever its kind.
                search: INITIAL_SEARCH_STATE,
                operation: null,
                draft: null
            };
        }
        case CUSTOMER_CARD_APPLY_FAILED:
            return {
                ...state,
                saveStatus: 'error',
                saveError: action.payload.errorMessage
            };
        // User explicitly cancelled (e.g. confirmed discard in the discard modal).
        // In the future this action may carry additional intent (e.g. close panel, navigate away).
        case CUSTOMER_CARD_CANCELLED:
            return {
                ...state,
                saveStatus: 'idle',
                saveError: undefined,
                creditConsentJustGranted: false,
                search: INITIAL_SEARCH_STATE,
                operation: null,
                draft: null
            };
        case SWAP_BUYER_COBUYER_REQUEST:
            return { ...state, swapStatus: 'saving', swapError: undefined };
        case SWAP_BUYER_COBUYER_SUCCESS:
            return { ...state, swapStatus: 'success', swapError: undefined };
        case SWAP_BUYER_COBUYER_FAILURE:
            return { ...state, swapStatus: 'error', swapError: action.payload.error };
        case SWAP_BUYER_COBUYER_RESET:
            return { ...state, swapStatus: 'idle', swapError: undefined };
        case DROP_CO_BUYER_REQUEST:
            return { ...state, dropCoBuyerStatus: 'saving', dropCoBuyerError: undefined };
        case DROP_CO_BUYER_SUCCESS:
            return { ...state, dropCoBuyerStatus: 'success', dropCoBuyerError: undefined };
        case DROP_CO_BUYER_FAILURE:
            return { ...state, dropCoBuyerStatus: 'error', dropCoBuyerError: action.payload.error };
        case DROP_CO_BUYER_RESET:
            return { ...state, dropCoBuyerStatus: 'idle', dropCoBuyerError: undefined };
        case SAVE_PARTICIPANTS_REQUEST:
            return { ...state, saveParticipantsError: undefined };
        case SAVE_PARTICIPANTS_SUCCESS:
            return {
                ...state,
                saveParticipantsError: action.payload?.ok === false ? action.payload : undefined
            };
        case SAVE_PARTICIPANTS_FAILURE:
            return { ...state, saveParticipantsError: action.payload };
        case SET_INSURANCE_PROVIDERS:
            return { ...state, insuranceProviders: action.payload };
        case SAVE_INSURANCE_REQUEST:
            return { ...state, saveInsuranceError: undefined };
        case SAVE_INSURANCE_SUCCESS:
            return {
                ...state,
                saveInsuranceError: action.payload?.ok === false ? action.payload : undefined
            };
        case SAVE_INSURANCE_FAILURE:
            return { ...state, saveInsuranceError: action.payload };

        case START_OPERATION:
            return {
                ...state,
                search: INITIAL_SEARCH_STATE,
                operation: { kind: action.payload, step: CARD_OPERATION_STEP.SEARCH, operationId: action.meta.operationId },
                draft: null
            };
        case SEARCH_REQUEST:
            return {
                ...state,
                search: {
                    ...state.search,
                    loading: true,
                    error: null,
                    selected: null,
                    term: action.payload,
                    attemptCount: state.search.attemptCount + 1
                }
            };
        case SEARCH_SUCCESS:
            return {
                ...state,
                search: { ...state.search, loading: false, results: action.payload }
            };
        case SEARCH_FAILURE:
            return {
                ...state,
                search: { ...state.search, loading: false, error: action.payload }
            };
        case SELECT_SEARCH_RESULT: {
            return {
                ...state,
                search: { ...state.search, selected: action.payload }
            };
        }
        case CLEAR_SEARCH_RESULTS:
            return {
                ...state,
                search: { ...state.search, results: [], selected: null, term: null }
            };
        case CONTINUE_OPERATION:
            // No operation to advance — a host wiring mistake, so leave state untouched.
            if (!state.operation) return state;
            return {
                ...state,
                operation: { ...state.operation, step: CARD_OPERATION_STEP.EDIT },
                draft: action.payload ?? null
            };
        case BACK:
            // Nothing to go back from — a host wiring mistake, so leave state untouched.
            if (!state.operation) return state;
            // From `edit`, back returns to the results, which stay selected. From `search` there is
            // nowhere further back, so the operation ends.
            return state.operation.step === CARD_OPERATION_STEP.EDIT
                ? { ...state, operation: { ...state.operation, step: CARD_OPERATION_STEP.SEARCH }, draft: null }
                : { ...state, search: INITIAL_SEARCH_STATE, operation: null, draft: null };
        case CANCEL_CARD_OPERATION:
            return {
                ...state,
                search: INITIAL_SEARCH_STATE,
                operation: null,
                draft: null
            };

        default:
            return state;
    }
};
